import { DefineOwnModule, RootActions } from 'stores/type';
import EventsManager from 'managers/events.manager';
import StorageManager, { StorageKey } from 'managers/storage.manager';
import ScrollManager from 'managers/scroll.manager';
import HistoryManager from 'managers/history.manager';
import { createParentsKey } from 'helpers/history';
import { isDlcElement } from 'helpers/dlc';
import { State, Getters, Actions, Mutations } from './types';

const moduleState: State = {
  historyTimestamp: 0,
  resetTimestamp: StorageManager.load(StorageKey.RESET_TIMESTAMP) || -1,
};

const storeModule: DefineOwnModule<State, Getters, Mutations, Actions & Pick<RootActions, 'mixNew'>> = {
  state: moduleState,

  getters: {
    history: (state) => {
      if (state.historyTimestamp < 0) return [];

      return HistoryManager.history;
    },
    historyMap: (state) => {
      if (state.historyTimestamp < 0) return {};

      return HistoryManager.historyMap;
    },
    hasMixed: (_state, getters) => (parents) => {
      const key = createParentsKey(parents);
      return getters.historyMap.hasOwnProperty(key);
    },
    historyBefore: (_state, getters) => (before) =>
      getters.history.filter(({ timestamp }) => timestamp < before),
    historyLastTimestamp: (_state, getters) => {
      if (!getters.history.length) return 0;

      return getters.history[getters.history.length - 1].timestamp;
    },

    resetTimestamp: (state) => state.resetTimestamp,
  },

  actions: {
    historyReload({ dispatch }) {
      dispatch('progressCalculate');
      ScrollManager.scrollTop();
      EventsManager.emit('SYNC_PROGRESS_CHANGED');
    },
    historySet({ commit }, history) {
      commit('HISTORY_SET', history);
    },
    historyIntegrity({ getters, dispatch }) {
      const parentsExist = (id: string) => (getters.hasElement(id) && getters.hasInProgress(id)) || isDlcElement(id);

      const history = getters.history.filter(
        ({ mother, father }) => parentsExist(mother) && parentsExist(father),
      );

      const historyChanged = history.length !== getters.history.length;
      if (!historyChanged) return;

      // some elements without parents were found in history -> set new history
      dispatch('historySet', history);
      dispatch('historyReload');
    },
    historyRemoveBeforeTimestamp({ commit }, timestamp) {
      commit('HISTORY_REMOVE_BEFORE', timestamp);
    },

    mixNew({ commit, getters }, { parents }) {
      if (getters.hasMixed(parents)) return;

      commit('HISTORY_NEW_PARENTS', parents);
      EventsManager.emit('HISTORY_UPDATE');
    },

    resetProgress({ commit, dispatch }) {
      commit('RESET_PROGRESS');
      dispatch('libraryReload');

      EventsManager.emit('PROGRESS_RESET');
    },
  },

  mutations: {
    HISTORY_SET(state, history) {
      HistoryManager.set(history);
      state.historyTimestamp = Date.now();
    },
    HISTORY_ADD(state, data) {
      HistoryManager.addEntry(data);
      state.historyTimestamp = Date.now();
    },
    HISTORY_NEW_PARENTS(state, parents) {
      HistoryManager.addNewParents(parents);
      state.historyTimestamp = Date.now();
    },
    HISTORY_REMOVE_BEFORE(state, before) {
      HistoryManager.removeBeforeTimestamp(before);
      state.historyTimestamp = Date.now();
    },

    RESET_PROGRESS(state) {
      HistoryManager.reset();

      state.historyTimestamp = Date.now();
      state.resetTimestamp = Date.now();
    },
    RESET_TIMESTAMP(state, timestamp) {
      state.resetTimestamp = timestamp;
    },
  },
};

export default storeModule;
